// components/CreateSpaceForm.jsx — Anamoria SPA
// v0.7 — NEW-171 Phase 3 (SPA PR-2 / 2c): optional groupParam prop ('' default). When set
//   (e.g. '?group=<id>' from the leader dashboard switcher) it is appended to POST /spaces so a
//   multi-cohort leader creates the group space for the SELECTED cohort (backend anamoria-spaces
//   v1.23, #162 — membership-verified). Default '' = home cohort = byte-identical to v0.6. (Nov 2, 2026)
// 2026-10-18: optional initialSpaceType prop — caller can pre-select Private/Group (the leader
//   "+ New group space" entry opens the create modal with 'group'). Default unchanged.
// E4b (Oct 4, 2026; NEW-99): quiet-note checkbox (QN-5) under each filled date when
//   key_date_note_capture_enabled AND the member_key_date_note notice is stored; sends noteChoices +
//   keyDates[].wantsNote + one noteShownHash on POST. Dark otherwise (byte-identical).
// v0.6 (Sept 17, 2026): privacy note ("This space will be private") is now leader-aware — hidden
//   for leaders (who may create a group space), matching the modal. Consolidation prep so the modal
//   can render this form without regressing that behavior.
// E1 (Oct 4, 2026): RELATIONSHIP_OPTIONS now imported from src/constants/relationships.js (14 entries).
// v0.5 addition (Sept 17, 2026): NEW-96 Create-a-Space relationship + "days to hold close".
//   Behind feature flag relationship_key_dates_enabled (appState.flags, fail-closed). When on,
//   captures relationship (coded + free-text "other"), their birthday, the day they passed, and
//   open-ended custom dates; sends relationship/relationshipDetail/birthdate/dateOfLoss/keyDates
//   on POST /spaces (backend anamoria-spaces v1.14). Flag off = byte-identical to v0.4. The
//   per-date "quiet note" is Phase 2 (NEW-99), intentionally not shown here.
// v0.4 addition (Sept 3, 2026): space-type control is now a segmented control (Private/Group)
//   with brand-voice copy + helper line; leader-only, creation logic unchanged.
// v0.3 addition (Sept 1, 2026): leaders choose Private vs Group space at creation
//   (sends isGroupSpace; backend enforces leader-only). Members always private.
//   Type-choice copy + styling are PLACEHOLDER pending brand-voice + design review.
// v1.0 — May 1, 2026
//
// Purpose:
//   Shared form body for creating a Space. Renders the prompt, name input,
//   photo upload row, CTA button, and privacy footer. Owns all state and
//   the create-space + S3 photo upload flow.
//
//   Currently consumed by:
//     - src/pages/CreateSpacePage.jsx (full-page route at /spaces/new)
//
//   The CreateSpaceModal v1.2 still has its own inline copy of this logic.
//   When the modal is next touched, replace its inline body+cta with
//   <CreateSpaceForm/> and remove the inline copy.
//
// Logic source: ported verbatim from CreateSpaceModal.jsx v1.2.
//   - Same state (name, photoFile, photoPreview, creating, error)
//   - Same client-side validation (image/* MIME, ≤ 5MB)
//   - Same 3-step S3 upload (POST /media/upload-url → PUT to S3 → PATCH /spaces/:id)
//   - Same AppContext sidebar update
//   - Photo failure does NOT block space creation (graceful degradation)
//
// Props:
//   getApi      function     Required. Returns API client. Same shape modal expects.
//   onSuccess   function     Required. Called with newSpaceId after successful creation.
//                            Caller is responsible for navigation / closing.
//   ctaLabel    string       Optional. Defaults to "Next". Page passes "Create space".
//   autoFocus   boolean      Optional. Defaults to true.
//   groupParam  string       Optional. '' default. Query string ('?group=<id>') appended to
//                            POST /spaces so a group space is created for a selected non-home cohort.

import { useState, useCallback, useRef, useMemo } from 'react';
import { useAppContext } from '../App';
import { useConsentNotice } from '../hooks/useConsentNotice';
import styles from './CreateSpaceForm.module.css';
import { RELATIONSHIP_OPTIONS } from '../constants/relationships'; // E1: shared 14-entry list

const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB

// E1 (Consent PR E, Oct 4, 2026): the relationship list moved to src/constants/relationships.js (shared with
// Settings and the contributor invite form) and grew to 14 entries (copy v0.3 §5.1). Re-exported here so
// existing imports keep working.
export { RELATIONSHIP_OPTIONS };

const MAX_KEY_DATES = 20;

// Shared inline styles (this file styles new fields inline, matching the v0.4 space-type control)
const fieldLabelStyle = { display: 'block', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#9a9082', margin: '0 2px 8px' };
const optionalTagStyle = { textTransform: 'none', letterSpacing: 0, color: '#a79d8e', fontWeight: 500, fontSize: 12 };
const creamFieldStyle = { width: '100%', padding: '14px 16px', fontSize: 16, borderRadius: 12, border: '1.5px solid #e0ddd8', background: 'var(--color-bg, #faf9f7)', color: 'var(--color-text-primary, #2d3436)', fontFamily: "var(--font-sans, 'DM Sans', -apple-system, sans-serif)", outline: 'none', boxSizing: 'border-box' };

export default function CreateSpaceForm({
  getApi,
  onSuccess,
  ctaLabel = 'Next',
  autoFocus = true,
  initialSpaceType,
  groupParam = '',
}) {
  const appState = useAppContext();
  const fileInputRef = useRef(null);

  const [name, setName] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [spaceType, setSpaceType] = useState(initialSpaceType === 'group' ? 'group' : 'private'); // v0.3: leader-only group/private choice; honors initialSpaceType from caller

  // v0.5: relationship + key dates (gated by relationship_key_dates_enabled, fail-closed)
  const relKeyDatesEnabled = appState?.flags?.relationship_key_dates_enabled === true;
  const [relationship, setRelationship] = useState('');
  const [relationshipDetail, setRelationshipDetail] = useState('');
  const [birthday, setBirthday] = useState('');
  const [dayOfLoss, setDayOfLoss] = useState('');
  const [keyDates, setKeyDates] = useState([]); // [{ label, date, wantsNote }]
  const [datesOpen, setDatesOpen] = useState(false);

  // E4b (NEW-99): quiet-note choices (gated by key_date_note_capture_enabled + stored notice, fail-closed)
  const noteEnabled = appState?.flags?.key_date_note_capture_enabled === true;
  const noteApi = useMemo(() => getApi(), [getApi]);
  const noteNotice = useConsentNotice(noteApi, 'member_key_date_note', noteEnabled);
  const [noteChoices, setNoteChoices] = useState({ birthday: false, dayTheyPassed: false });
  const [noteOpen, setNoteOpen] = useState(false);

  const addKeyDate = useCallback(() => {
    setKeyDates((prev) => (prev.length >= MAX_KEY_DATES ? prev : [...prev, { label: '', date: '', wantsNote: false }]));
  }, []);
  const updateKeyDate = useCallback((i, patch) => {
    setKeyDates((prev) => prev.map((kd, idx) => (idx === i ? { ...kd, ...patch } : kd)));
  }, []);
  const removeKeyDate = useCallback((i) => {
    setKeyDates((prev) => prev.filter((_, idx) => idx !== i));
  }, []);

  const isDisabled = !name.trim() || creating;

  /* ─── Photo upload ─── */

  const handlePhotoClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handlePhotoChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      setError('Photo must be under 5MB.');
      return;
    }

    setError('');
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  }, []);

  const handleRemovePhoto = useCallback(() => {
    setPhotoPreview(null);
    setPhotoFile(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  /* ─── Upload photo to S3 and save key to space ─── */

  async function uploadSpacePhoto(api, spaceId, file) {
    // Step 1: Get pre-signed upload URL
    const mimeType = file.type || 'image/jpeg';
    const ext = mimeType.split('/')[1] || 'jpg';
    const fileName = `space-photo.${ext}`;

    const uploadData = await api.post('/media/upload-url', {
      spaceId,
      albumId: null,
      fileName,
      mimeType,
      mediaType: 'photo',
    });

    if (!uploadData?.uploadUrl || !uploadData?.s3Key) {
      throw new Error('Failed to get upload URL');
    }

    // Step 2: PUT file to S3
    const putResponse = await fetch(uploadData.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': mimeType },
      body: file,
    });

    if (!putResponse.ok) {
      throw new Error(`S3 upload failed: ${putResponse.status}`);
    }

    // Step 3: Save S3 key to space
    await api.patch(`/spaces/${spaceId}`, {
      photoUrl: uploadData.s3Key,
    });

    return uploadData.s3Key;
  }

  /* ─── Create space ─── */

  const handleCreate = useCallback(async () => {
    if (isDisabled) return;
    setCreating(true);
    setError('');
    try {
      const api = getApi();
      const isLeader = appState?.pilotRole === 'leader';
      const payload = {
        name: name.trim(),
        isPrivate: true,
        // v0.3: only a leader may create a group space (backend enforces this too).
        ...(isLeader && spaceType === 'group' ? { isGroupSpace: true } : {}),
      };
      // v0.5: relationship + key dates — only sent when the flag is on
      if (relKeyDatesEnabled) {
        if (relationship) payload.relationship = relationship;
        if (relationship === 'other' && relationshipDetail.trim()) {
          payload.relationshipDetail = relationshipDetail.trim();
        }
        if (birthday) payload.birthdate = birthday;
        if (dayOfLoss) payload.dateOfLoss = dayOfLoss;
        const noteReady = noteEnabled && noteNotice.ready;
        const cleanKeyDates = keyDates
          .filter((kd) => kd.label.trim() && kd.date)
          .map((kd) => ({ label: kd.label.trim(), date: kd.date, ...(noteReady ? { wantsNote: kd.wantsNote === true } : {}) }));
        if (cleanKeyDates.length) payload.keyDates = cleanKeyDates;
        // E4b: quiet-note choices + the shown-wording hash (a yes needs the hash — B2). Dark unless noteReady.
        if (noteReady) {
          const nb = noteChoices.birthday && !!birthday;
          const np = noteChoices.dayTheyPassed && !!dayOfLoss;
          payload.noteChoices = { birthday: nb, dayTheyPassed: np };
          if (nb || np || cleanKeyDates.some((k) => k.wantsNote === true)) payload.noteShownHash = noteNotice.shownHash;
        }
      }
      const result = await api.post(`/spaces${groupParam}`, payload);

      const newSpaceId = result.id || result.spaceId;
      if (!newSpaceId) {
        throw new Error('No space ID returned');
      }

      // Upload photo if one was selected
      let savedPhotoUrl = null;
      if (photoFile) {
        try {
          savedPhotoUrl = await uploadSpacePhoto(api, newSpaceId, photoFile);
        } catch (photoError) {
          console.error('Space photo upload failed:', photoError);
          // Photo failure does not block space creation.
          // User can add photo later via Space Settings.
        }
      }

      // Update spaces list in AppContext so sidebar reflects the new space
      // without requiring a page reload or /spaces re-fetch.
      if (appState?.updateSpaces && appState?.spaces) {
        const newSpace = {
          id: newSpaceId,
          name: name.trim(),
          privacyMode: 'private',
          photoUrl: savedPhotoUrl,
          isPinned: false,
          pinnedAt: null,
        };
        appState.updateSpaces([...appState.spaces, newSpace]);
      }

      // Hand off to caller. Caller decides what to do (close modal, navigate, etc.)
      onSuccess(newSpaceId);
    } catch (err) {
      console.error('Create space error:', err);
      setError('Could not create space. Please try again.');
      setCreating(false);
    }
    // NOTE: do not setCreating(false) on success — onSuccess unmounts this
    // component (page navigates / modal closes). Setting state on an unmounted
    // component would warn in dev. Same pattern as CreateSpaceModal v1.2.
  }, [isDisabled, name, photoFile, getApi, onSuccess, appState, spaceType, relKeyDatesEnabled, relationship, relationshipDetail, birthday, dayOfLoss, keyDates, noteEnabled, noteNotice.ready, noteNotice.shownHash, noteChoices]);

  return (
    <>
      {/* Body */}
      <div className={styles.body}>
        <p className={styles.prompt}>Who is this space for?</p>

        <input
          type="text"
          className={styles.input}
          placeholder="Their name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          autoFocus={autoFocus}
        />

        {/* v0.5: who the loved one is to the creator — optional, coded vocabulary + free-text "other" */}
        {relKeyDatesEnabled && (
          <div style={{ width: '100%', marginTop: 18 }}>
            <label htmlFor="cs-relationship" style={fieldLabelStyle}>
              Who they are to you <span style={optionalTagStyle}>(optional)</span>
            </label>
            <select
              id="cs-relationship"
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              style={creamFieldStyle}
            >
              <option value="">Choose one</option>
              {RELATIONSHIP_OPTIONS.map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
            {relationship === 'other' && (
              <input
                type="text"
                value={relationshipDetail}
                onChange={(e) => setRelationshipDetail(e.target.value)}
                placeholder="In your own words"
                maxLength={160}
                style={{ ...creamFieldStyle, marginTop: 8 }}
              />
            )}
          </div>
        )}

        {/* v0.4: leaders choose the space type at creation (segmented control).
            Copy passed Anamoria brand-voice review; members always private. */}
        {appState?.pilotRole === 'leader' && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#9a9082', margin: '0 2px 8px' }}>Space type</div>
            <div role="radiogroup" aria-label="Space type" style={{ display: 'flex', gap: 5, padding: 5, background: '#F4F0E9', borderRadius: 13 }}>
              {[['private', 'Private'], ['group', 'Group']].map(([val, label]) => {
                const selected = spaceType === val;
                return (
                  <button
                    key={val}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setSpaceType(val)}
                    style={{
                      flex: 1, padding: '11px 12px', borderRadius: 9, border: 'none', cursor: 'pointer',
                      fontWeight: 600, fontSize: '14.5px',
                      background: selected ? '#FFFFFF' : 'transparent',
                      color: selected ? '#744D2E' : '#8a7f70',
                      boxShadow: selected ? '0 1px 3px rgba(58,53,45,0.10)' : 'none',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p style={{ fontSize: 13, lineHeight: 1.45, color: '#737373', margin: '10px 2px 0' }}>A private space is just for you and the people you invite. In a group space, you see what contributors add and choose what to share with the group.</p>
          </div>
        )}

        {/* v0.5: days to hold close — optional, collapsed by default (per mockup v0.6) */}
        {relKeyDatesEnabled && (
          <div style={{ width: '100%', marginTop: 18 }}>
            <button
              type="button"
              onClick={() => setDatesOpen((o) => !o)}
              aria-expanded={datesOpen}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 16px', borderRadius: 12, border: '1.5px solid #e0ddd8', background: 'var(--color-bg, #faf9f7)', cursor: 'pointer', textAlign: 'left', fontFamily: "var(--font-sans, 'DM Sans', -apple-system, sans-serif)" }}
            >
              <span>
                <span style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text-primary, #2d3436)' }}>Days to hold close <span style={optionalTagStyle}>(optional)</span></span>
                <span style={{ display: 'block', fontSize: 12.5, color: '#8a7f70', marginTop: 3 }}>Birthdays, anniversaries, and other days</span>
              </span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#a59a89" strokeWidth="2" strokeLinecap="round" style={{ flex: '0 0 auto', transform: datesOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {datesOpen && (
              <div style={{ marginTop: 12 }}>
                <p style={{ fontSize: 13, lineHeight: 1.5, color: '#737373', margin: '0 2px 12px' }}>If there are days you&apos;d like to hold close, you can add them here.</p>
                {noteEnabled && noteNotice.ready && (
                  <div style={{ margin: '0 2px 14px' }}>
                    <p style={{ fontSize: 13, lineHeight: 1.5, color: '#737373', margin: '0 0 2px' }}>Only ever to you, and you can change these anytime.</p>
                    <button type="button" onClick={() => setNoteOpen((o) => !o)} aria-expanded={noteOpen} style={{ background: 'none', border: 'none', padding: '2px 0', fontSize: 13, color: 'var(--color-sage-dark, #5b7a65)', textDecoration: 'underline', cursor: 'pointer' }}>What this means</button>
                    {noteOpen && (
                      <div style={{ margin: '8px 0 0', padding: '12px 14px', borderRadius: 10, background: 'var(--color-bg, #faf9f7)', border: '1px solid #e0ddd8' }}>
                        {noteNotice.body.split(/\n{2,}/).map((x) => x.trim()).filter(Boolean).map((para, i) => <p key={i} style={{ margin: '0 0 8px', fontSize: 13.5, lineHeight: 1.5, color: '#2d3436' }}>{para}</p>)}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 15, color: 'var(--color-text-primary, #2d3436)' }}>
                    Their birthday
                    <input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} style={{ ...creamFieldStyle, width: 'auto', padding: '10px 12px', fontSize: 15 }} />
                  </label>
                  {noteEnabled && noteNotice.ready && birthday && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '-4px 2px 2px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={noteChoices.birthday} onChange={(e) => setNoteChoices((n) => ({ ...n, birthday: e.target.checked }))} aria-label="Send me a quiet note on this day" style={{ width: 18, height: 18, flex: '0 0 auto', accentColor: 'var(--color-sage-dark, #5b7a65)' }} />
                      <span style={{ fontSize: 13, color: '#737373' }}>Send me a quiet note on this day</span>
                    </label>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 15, color: 'var(--color-text-primary, #2d3436)' }}>
                    The day they passed
                    <input type="date" value={dayOfLoss} onChange={(e) => setDayOfLoss(e.target.value)} style={{ ...creamFieldStyle, width: 'auto', padding: '10px 12px', fontSize: 15 }} />
                  </label>
                  {noteEnabled && noteNotice.ready && dayOfLoss && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '-4px 2px 2px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={noteChoices.dayTheyPassed} onChange={(e) => setNoteChoices((n) => ({ ...n, dayTheyPassed: e.target.checked }))} aria-label="Send me a quiet note on this day" style={{ width: 18, height: 18, flex: '0 0 auto', accentColor: 'var(--color-sage-dark, #5b7a65)' }} />
                      <span style={{ fontSize: 13, color: '#737373' }}>Send me a quiet note on this day</span>
                    </label>
                  )}

                  {keyDates.map((kd, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="text"
                          value={kd.label}
                          onChange={(e) => updateKeyDate(i, { label: e.target.value })}
                          placeholder="Name this day"
                          maxLength={120}
                          style={{ ...creamFieldStyle, flex: 1, padding: '10px 12px', fontSize: 15 }}
                        />
                        <input
                          type="date"
                          value={kd.date}
                          onChange={(e) => updateKeyDate(i, { date: e.target.value })}
                          style={{ ...creamFieldStyle, width: 'auto', padding: '10px 12px', fontSize: 15 }}
                        />
                        <button type="button" onClick={() => removeKeyDate(i)} aria-label="Remove this day" style={{ flex: '0 0 auto', border: 'none', background: 'none', cursor: 'pointer', color: '#a59a89', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
                      </div>
                      {noteEnabled && noteNotice.ready && kd.label.trim() && kd.date && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 2px 2px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={kd.wantsNote === true} onChange={(e) => updateKeyDate(i, { wantsNote: e.target.checked })} aria-label="Send me a quiet note on this day" style={{ width: 18, height: 18, flex: '0 0 auto', accentColor: 'var(--color-sage-dark, #5b7a65)' }} />
                          <span style={{ fontSize: 13, color: '#737373' }}>Send me a quiet note on this day</span>
                        </label>
                      )}
                    </div>
                  ))}
                </div>

                {keyDates.length < MAX_KEY_DATES && (
                  <button type="button" onClick={addKeyDate} style={{ marginTop: 12, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-sage-dark, #5b7a65)', fontWeight: 600, fontSize: 14.5, fontFamily: "var(--font-sans, 'DM Sans', -apple-system, sans-serif)", display: 'flex', alignItems: 'center', gap: 6, padding: '2px 2px' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                    Add another day
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Photo upload */}
        <div className={styles.photoRow}>
          {!photoPreview ? (
            <>
              <button
                type="button"
                className={styles.photoDashed}
                onClick={handlePhotoClick}
                aria-label="Add a photo"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  stroke="currentColor"
                  width="24"
                  height="24"
                  style={{ opacity: 0.45 }}
                >
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </button>
              <span className={styles.photoHelper}>Add a photo (optional)</span>
            </>
          ) : (
            <div className={styles.photoPreviewWrap}>
              <img src={photoPreview} alt="Preview" className={styles.photoPreviewImg} />
              <button
                type="button"
                className={styles.photoChange}
                onClick={handleRemovePhoto}
              >
                Change photo
              </button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className={styles.fileInput}
          />
        </div>
      </div>

      {/* CTA */}
      <div className={styles.cta}>
        {error && <p className={styles.error} role="alert">{error}</p>}

        <button
          type="button"
          className={styles.nextBtn}
          onClick={handleCreate}
          disabled={isDisabled}
        >
          {creating ? 'Creating...' : ctaLabel}
        </button>

        {/* Privacy note — a group space (leader) isn't private, so show it only when it's true. */}
        {appState?.pilotRole !== 'leader' && (
          <div className={styles.privacyNote}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" />
            </svg>
            <span>This space will be private</span>
          </div>
        )}
      </div>
    </>
  );
}
